// ---------------------------------------------------------------------------
// FlowStepCard — Renders a single ActionFlow step as a compact card.
//
// Shows step number, action icon, action label, target/params, and
// read/write data-access badges. Recursive for conditional/loop/etc.
// ---------------------------------------------------------------------------

import { useMemo } from 'react'
import {
  Play,
  Database,
  ArrowRight,
  MessageSquare,
  GitBranch,
  Variable,
  Repeat,
  GitFork,
  Layers,
  Shield,
  Zap,
  Code,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { FlowStep, BasicStep, ConditionalStep, LoopStep, SwitchStep, ParallelStep, TryCatchStep, StageStep, SagaStep } from '@cogo/types-v2'
import type { Narrative } from '@cogo/types'
import { cn } from '@/lib/utils'
import {
  getActionDisplayInfo,
  legacyRuntimeStatus,
  resolveLegacyActionAlias,
} from '@/features/flow-editor/lib/legacy-action-aliases'
import { useState, Fragment } from 'react'
import { AddStepLine } from './add-step-popover'
import { useFlowEditorStore } from '@/features/flow-editor/store/flow-editor-store'
import type { StepBranch, StepBranchPath, StepPath, NestedStepPath } from '@/features/flow-editor/store/flow-slice'
import { ChevronRight } from 'lucide-react'
import { buildActionExecutionModel } from '@/features/flow-editor/services/actionflow-execution-model'

// ---------------------------------------------------------------------------
// Action -> Icon mapping
// ---------------------------------------------------------------------------

const ACTION_ICON_MAP: Record<string, LucideIcon> = {
  compute: Variable,
  setState: Database,
  navigate: ArrowRight,
  upsertWhere: Database,
  showToast: MessageSquare,
  scrollToPosition: ArrowRight,
  conditional: GitBranch,
  restful_data_action: Zap,
  graphql_action: Zap,
  supabase_select: Database,
  supabase_insert: Database,
  supabase_update: Database,
  supabase_delete: Database,
  navigation_action: ArrowRight,
  go_to_link_action: ArrowRight,
  open_drawer_action: Layers,
  show_popup_action: MessageSquare,
  crypto_action: Shield,
  expression: Code,
}

function getStepIcon(step: FlowStep): LucideIcon {
  switch (step.type) {
    case 'conditional':
      return GitBranch
    case 'loop':
      return Repeat
    case 'switch':
      return GitFork
    case 'parallel':
      return Layers
    case 'tryCatch':
      return Shield
    case 'stage':
      return Layers
    case 'saga':
      return Shield
    default:
      break
  }

  if ('action' in step && typeof step.action === 'string') {
    // Legacy-named steps borrow their canonical equivalent's icon (QA-4).
    const alias = resolveLegacyActionAlias(step.action)
    return ACTION_ICON_MAP[step.action] ?? (alias ? ACTION_ICON_MAP[alias.canonicalAction] : undefined) ?? Play
  }

  return Play
}

// ---------------------------------------------------------------------------
// Data access badge extraction
// ---------------------------------------------------------------------------

interface DataBadge {
  scope: 'app' | 'system' | 'block'
  mode: 'R' | 'W'
}

function extractDataBadges(step: FlowStep): DataBadge[] {
  if (step.type === 'conditional' || step.type === 'loop' || step.type === 'switch' || step.type === 'parallel' || step.type === 'tryCatch' || step.type === 'stage' || step.type === 'saga') {
    return []
  }

  const badges: DataBadge[] = []
  const params = (step as BasicStep).params
  if (!params) return badges

  const seen = new Set<string>()

  const classify = (val: unknown, mode: 'R' | 'W') => {
    if (typeof val !== 'string') return
    let scope: 'app' | 'system' | 'block' | null = null
    if (val.startsWith('#appData.') || val.startsWith('#_appData.')) scope = 'app'
    else if (val.startsWith('#_system.')) scope = 'system'
    else if (val.startsWith('#') || val.match(/^[a-zA-Z_]\w*$/)) scope = 'block'
    if (scope) {
      const key = `${scope}:${mode}`
      if (!seen.has(key)) {
        seen.add(key)
        badges.push({ scope, mode })
      }
    }
  }

  // Heuristic: "target" is typically a write destination
  const writeKeys = ['target', 'to', 'destination', 'setState', 'key']
  const readKeys = ['source', 'from', 'value', 'condition', 'collection', 'path', 'url', 'body']

  for (const [k, v] of Object.entries(params)) {
    if (writeKeys.includes(k)) {
      classify(v, 'W')
    } else if (readKeys.includes(k)) {
      classify(v, 'R')
    } else {
      classify(v, 'R')
    }
  }

  return badges
}

// ---------------------------------------------------------------------------
// Badge component
// ---------------------------------------------------------------------------

function ScopeBadge({ badge }: { badge: DataBadge }) {
  const scopeLabel = badge.scope === 'app' ? 'app' : badge.scope === 'system' ? 'system' : 'block'
  const isWrite = badge.mode === 'W'

  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 px-1 py-0 rounded text-[8px] font-mono leading-tight',
        badge.scope === 'app' && 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
        badge.scope === 'system' && 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
        badge.scope === 'block' && 'bg-gray-500/10 text-gray-500 dark:text-gray-400',
      )}
    >
      <span
        className={cn(
          'font-bold',
          isWrite ? 'text-amber-700 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400',
        )}
      >
        {badge.mode}
      </span>
      {scopeLabel}
    </span>
  )
}

// ---------------------------------------------------------------------------
// FlowStepCard
// ---------------------------------------------------------------------------

export interface FlowStepCardProps {
  step: FlowStep
  index: number
  depth?: number
  /** Full editor address of THIS card's step. Top-level call sites may omit
   *  it (defaults to `[index]`); the nested recursion always provides it so a
   *  card at any depth can address its own branch positions. */
  path?: StepPath
  isSelected?: boolean
  onSelect?: (index: number) => void
  onNestedSelect?: (path: NestedStepPath) => void
  /** Narrative from parent flow — shown as header on first step (index === 0) */
  narrative?: Narrative | null
}

/** Editor addresses are positional — equality is element-wise. */
function pathsEqual(a: StepPath | null, b: StepPath): boolean {
  return a != null && a.length === b.length && a.every((seg: number | StepBranch, i: number) => seg === b[i])
}

const SAGA_STAGE_ACTIONS = ['stage'] as const

// ---------------------------------------------------------------------------
// NestedBranch — one labeled nested step array (then/else/try/catch/finally/loop body/
// switch case). Editable (AddStepLine + inspector selection) at ANY depth:
// StepPath addressing is recursive, so each child card receives its full
// `[...parentPath, branch, i]` address and inserts splice exactly the array
// this branch renders. (The old two-level addressing could not express
// depth >= 1 branch positions — adds there landed on the WRONG top-level
// step — so editing used to be gated to depth 0.)
// ---------------------------------------------------------------------------

function NestedBranch({
  label,
  labelClass,
  parentPath,
  branch,
  steps,
  editable,
  allowedActions,
  depth,
  onNestedSelect,
}: {
  label: string
  labelClass: string
  parentPath: StepPath
  branch: StepBranch
  steps: FlowStep[]
  editable: boolean
  allowedActions?: readonly string[]
  depth: number
  onNestedSelect?: (path: NestedStepPath) => void
}) {
  const branchPath = [...parentPath, branch] as StepBranchPath
  const selectedStepPath = useFlowEditorStore((s) => s.selectedStepPath)
  return (
    <div className="ml-2">
      <span className={cn('text-[9px] font-semibold uppercase tracking-wider px-1', labelClass)}>
        {label}
      </span>
      {editable && <AddStepLine index={0} nestedParent={branchPath} allowedActions={allowedActions} />}
      {steps.map((s, i) => {
        const childPath = [...branchPath, i] as unknown as NestedStepPath
        return (
          <Fragment key={i}>
            <FlowStepCard
              step={s}
              index={i}
              depth={depth + 1}
              path={childPath}
              isSelected={pathsEqual(selectedStepPath, childPath)}
              onSelect={editable ? () => onNestedSelect?.(childPath) : undefined}
              onNestedSelect={onNestedSelect}
            />
            {editable && <AddStepLine index={i + 1} nestedParent={branchPath} allowedActions={allowedActions} />}
          </Fragment>
        )
      })}
    </div>
  )
}

export function FlowStepCard({ step, index, depth = 0, path, isSelected, onSelect, onNestedSelect, narrative }: FlowStepCardProps) {
  const selfPath: StepPath = path ?? [index]
  const Icon = getStepIcon(step)
  const badges = useMemo(() => extractDataBadges(step), [step])
  const [detailsExpanded, setDetailsExpanded] = useState(true)

  // QA-4 display-only humanization: actions known to the legacy alias table
  // (both legacy names and their canonical sides) render the humanized label
  // with the raw action name demoted to secondary text — never hidden. A
  // legacy name with no live web executor additionally gets an honest
  // runtime-skip chip. Pure display: no step data changes here.
  // F17: both verdicts that mean "no executor on web" — 'skipped' (a resolver
  // key) and 'deferred-skip' (a DEFERRED_SKIP_ACTIONS name with no migrate
  // target) — surface the SAME honest skip chip; the card has no migrate
  // affordance to differentiate, so one chip covers both.
  const rawAction = 'action' in step ? (step as BasicStep).action : undefined
  const execution = useMemo(
    () => (rawAction ? buildActionExecutionModel(rawAction) : null),
    [rawAction],
  )
  const display = rawAction !== undefined ? getActionDisplayInfo(rawAction) : undefined
  const actionLabel = display?.label ?? (
    step.type === 'stage'
      ? `Stage: ${(step as StageStep).name}`
      : step.type === 'saga'
        ? 'Saga'
        : 'action' in step ? (step as BasicStep).action : step.type
  )
  const runtimeStatus = rawAction !== undefined ? legacyRuntimeStatus(rawAction) : undefined
  const runtimeSkipped = runtimeStatus === 'skipped' || runtimeStatus === 'deferred-skip'
  const params = 'params' in step ? (step as BasicStep).params : undefined
  const target = params?.target as string | undefined

  // Show narrative header only on the first step of a flow (depth 0, index 0)
  const showNarrative = narrative && index === 0 && depth === 0

  return (
    <div className={cn('flex flex-col gap-1', depth > 0 && 'ml-4')}>
      {/* Narrative header — shown above the first step */}
      {showNarrative && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 mb-1 space-y-1.5">
          <div className="text-xs font-semibold text-foreground">{narrative.effect}</div>
          {narrative.acceptance.length > 0 && (
            <ul className="space-y-0.5">
              {narrative.acceptance.map((clause, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[10px] text-muted-foreground">
                  <span className="shrink-0 mt-0.5">
                    {clause.kind === 'state-changed' || clause.kind === 'navigated' ? '●' : '○'}
                  </span>
                  <span>{clause.target}{('expect' in clause && clause.expect != null) ? ` = ${clause.expect}` : ''}</span>
                </li>
              ))}
            </ul>
          )}
          <button
            className="flex items-center gap-1 text-[10px] text-muted-foreground/60 hover:text-muted-foreground transition-colors"
            onClick={(e) => { e.stopPropagation(); setDetailsExpanded((v) => !v) }}
          >
            <ChevronRight className={cn('h-2.5 w-2.5 transition-transform', detailsExpanded && 'rotate-90')} />
            Details
          </button>
        </div>
      )}

      {/* Main card — collapsible when narrative is present. Click affordance
          (pointer cursor + hover) only when the card is actually selectable
          (onSelect present — true for every branch child since the recursive
          StepPath generalization). */}
      {(!showNarrative || detailsExpanded) && <div
        data-testid="flow-step-card"
        data-step-path={selfPath.join('.')}
        className={cn(
          'flex items-start gap-2 rounded-lg border px-2.5 py-2 transition-colors',
          onSelect && 'cursor-pointer',
          isSelected
            ? 'border-primary bg-primary/5 shadow-sm'
            : cn('border-border bg-card', onSelect && 'hover:bg-accent/30'),
        )}
        onClick={onSelect ? () => onSelect(index) : undefined}
      >
        {/* Step number */}
        <span className="shrink-0 flex items-center justify-center w-5 h-5 rounded-md bg-muted text-[10px] font-semibold text-muted-foreground mt-0.5">
          {index + 1}
        </span>

        {/* Icon */}
        <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground mt-0.5" />

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium truncate">{actionLabel}</span>
            {display?.raw && (
              <span
                data-testid="step-card-raw-action"
                className="text-[9px] font-mono text-muted-foreground/70 truncate"
              >
                {display.raw}
              </span>
            )}
            {runtimeSkipped && (
              <span
                data-testid="step-card-runtime-skip-chip"
                title={`The web runtime has no executor for "${rawAction}" — this step is skipped in Preview until it is updated.`}
                className="shrink-0 inline-flex items-center px-1 py-0 rounded bg-amber-500/15 text-[8px] font-medium text-amber-700 dark:text-amber-400"
              >
                skipped on web
              </span>
            )}
            {execution && (
              <span
                data-testid="step-card-action-lane"
                className={cn(
                  'shrink-0 inline-flex items-center rounded px-1 py-0 text-[8px] font-semibold uppercase tracking-wide',
                  execution.lane === 'action' && 'bg-slate-500/10 text-slate-600 dark:text-slate-300',
                  execution.lane === 'ui' && 'bg-sky-500/10 text-sky-700 dark:text-sky-300',
                  execution.lane === 'motion' && 'bg-violet-500/10 text-violet-700 dark:text-violet-300',
                )}
              >
                {execution.lane}
              </span>
            )}
            {execution?.completion === 'completed' && (
              <span
                data-testid="step-card-completion-barrier"
                title="The next step waits for this action to complete."
                className="shrink-0 inline-flex items-center rounded border border-violet-200 bg-violet-50 px-1 py-0 text-[8px] font-medium text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300"
              >
                waits
              </span>
            )}
            {/* Badges */}
            {badges.length > 0 && (
              <div className="flex items-center gap-0.5 shrink-0">
                {badges.map((b, i) => (
                  <ScopeBadge key={`${b.scope}-${b.mode}-${i}`} badge={b} />
                ))}
              </div>
            )}
          </div>

          {/* Target / key param */}
          {target && (
            <div className="text-[10px] text-muted-foreground truncate font-mono mt-0.5">
              {target}
            </div>
          )}

          {/* For conditional: show condition expression */}
          {step.type === 'conditional' && (
            <div className="text-[10px] text-muted-foreground truncate font-mono mt-0.5">
              if: {(step as ConditionalStep).condition}
            </div>
          )}

          {/* For loop: show collection */}
          {step.type === 'loop' && (
            <div className="text-[10px] text-muted-foreground truncate font-mono mt-0.5">
              each: {(step as LoopStep).itemVar} in {(step as LoopStep).collection}
            </div>
          )}

          {/* For switch: show value */}
          {step.type === 'switch' && (
            <div className="text-[10px] text-muted-foreground truncate font-mono mt-0.5">
              switch: {(step as SwitchStep).value}
            </div>
          )}

          {/* Key params display (expression, url, method, message, route, etc.) */}
          {params && (
            <div className="mt-1 space-y-0.5">
              {Object.entries(params).map(([key, val]) => {
                if (key === 'target') return null // already shown above
                if (val === undefined || val === null || val === '') return null
                const valStr = typeof val === 'object' ? JSON.stringify(val) : String(val)
                if (valStr.length === 0) return null
                return (
                  <div key={key} className="flex items-start gap-1 text-[10px]">
                    <span className="text-muted-foreground/60 shrink-0">{key}:</span>
                    <span className="font-mono text-muted-foreground break-all line-clamp-2">{valStr}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>}

      {/* Nested steps for conditional */}
      {step.type === 'conditional' && (
        <>
          <NestedBranch
            label="then"
            labelClass="text-primary dark:text-primary"
            parentPath={selfPath}
            branch="then"
            steps={(step as ConditionalStep).then}
            editable
            depth={depth}
            onNestedSelect={onNestedSelect}
          />
          <NestedBranch
            label="else"
            labelClass="text-amber-600 dark:text-amber-400"
            parentPath={selfPath}
            branch="else"
            steps={(step as ConditionalStep).else ?? []}
            editable
            depth={depth}
            onNestedSelect={onNestedSelect}
          />
        </>
      )}

      {/* Nested steps for loop — body rendered even when empty so the first
          step can be added in place (F33) */}
      {step.type === 'loop' && (
        <NestedBranch
          label="loop body"
          labelClass="text-blue-600 dark:text-blue-400"
          parentPath={selfPath}
          branch="steps"
          steps={(step as LoopStep).steps}
          editable
          depth={depth}
          onNestedSelect={onNestedSelect}
        />
      )}

      {/* Nested steps for parallel — body rendered even when empty so the
          first step can be added in place (catalog now offers parallel; the
          runtime engine has always executed it) */}
      {step.type === 'parallel' && (
        <NestedBranch
          label="parallel"
          labelClass="text-indigo-600 dark:text-indigo-400"
          parentPath={selfPath}
          branch="steps"
          steps={(step as ParallelStep).steps}
          editable
          depth={depth}
          onNestedSelect={onNestedSelect}
        />
      )}

      {/* Nested steps for tryCatch */}
      {step.type === 'tryCatch' && (
        <>
          <NestedBranch
            label="try"
            labelClass="text-primary dark:text-primary"
            parentPath={selfPath}
            branch="try"
            steps={(step as TryCatchStep).try}
            editable
            depth={depth}
            onNestedSelect={onNestedSelect}
          />
          <NestedBranch
            label="catch"
            labelClass="text-red-600 dark:text-red-400"
            parentPath={selfPath}
            branch="catch"
            steps={(step as TryCatchStep).catch ?? []}
            editable
            depth={depth}
            onNestedSelect={onNestedSelect}
          />
          <NestedBranch
            label="finally"
            labelClass="text-amber-600 dark:text-amber-400"
            parentPath={selfPath}
            branch="finally"
            steps={(step as TryCatchStep).finally ?? []}
            editable
            depth={depth}
            onNestedSelect={onNestedSelect}
          />
        </>
      )}

      {step.type === 'stage' && (() => {
        const stage = step as StageStep
        const branches: Array<{ branch: StepBranch; label: string; className: string; steps: FlowStep[] }> = [
          { branch: 'onEnter', label: 'on enter', className: 'text-blue-600 dark:text-blue-400', steps: stage.onEnter ?? [] },
          { branch: 'steps', label: 'stage body', className: 'text-primary dark:text-primary', steps: stage.steps },
          { branch: 'onSuccess', label: 'on success', className: 'text-success', steps: stage.onSuccess ?? [] },
          { branch: 'onError', label: 'on error', className: 'text-red-600 dark:text-red-400', steps: stage.onError ?? [] },
          { branch: 'onFinally', label: 'on finally', className: 'text-amber-600 dark:text-amber-400', steps: stage.onFinally ?? [] },
          { branch: 'compensate', label: 'compensate', className: 'text-violet-600 dark:text-violet-400', steps: stage.compensate ?? [] },
        ]
        return branches.map((item) => (
          <NestedBranch
            key={item.branch}
            label={item.label}
            labelClass={item.className}
            parentPath={selfPath}
            branch={item.branch}
            steps={item.steps}
            editable
            depth={depth}
            onNestedSelect={onNestedSelect}
          />
        ))
      })()}

      {step.type === 'saga' && (
        <NestedBranch
          label="ordered stages"
          labelClass="text-violet-600 dark:text-violet-400"
          parentPath={selfPath}
          branch="stages"
          steps={(step as SagaStep).stages}
          editable
          allowedActions={SAGA_STAGE_ACTIONS}
          depth={depth}
          onNestedSelect={onNestedSelect}
        />
      )}

      {/* Nested steps for switch — every case gets its own add line (F41);
          the default branch is shown even when absent so its first step can
          be added (insertNestedStep materializes it) */}
      {step.type === 'switch' && (
        <>
          {Object.entries((step as SwitchStep).cases).map(([caseKey, caseSteps]) => (
            <NestedBranch
              key={caseKey}
              label={`case: ${caseKey}`}
              labelClass="text-violet-600 dark:text-violet-400"
              parentPath={selfPath}
              branch={`case:${caseKey}`}
              steps={caseSteps}
              editable
              depth={depth}
              onNestedSelect={onNestedSelect}
            />
          ))}
          <NestedBranch
            label="default"
            labelClass="text-gray-600 dark:text-gray-400"
            parentPath={selfPath}
            branch="default"
            steps={(step as SwitchStep).default ?? []}
            editable
            depth={depth}
            onNestedSelect={onNestedSelect}
          />
        </>
      )}
    </div>
  )
}
